export const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString('ru-RU') : '—');

export const fmtMoney = (v?: number | null) =>
  v == null ? '—' : v.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtNum = (v?: number | null, digits = 0) =>
  v == null ? '—' : v.toLocaleString('ru-RU', { maximumFractionDigits: digits });

/** «через 3 ч», «просрочен» — для сроков сборки */
export function fmtDeadline(v?: string | null): { text: string; color?: string } {
  if (!v) return { text: '—' };
  const diffH = (new Date(v).getTime() - Date.now()) / 3_600_000;
  if (diffH < 0) return { text: `просрочен ${fmtDateTime(v)}`, color: 'red' };
  if (diffH < 6) return { text: `через ${Math.max(1, Math.round(diffH))} ч`, color: 'orange' };
  return { text: fmtDateTime(v) };
}

export const pluralOrders = (n: number) =>
  `${n} ${n % 10 === 1 && n % 100 !== 11 ? 'заказ' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'заказа' : 'заказов'}`;

/**
 * Артикул «1C-…» или голый GTIN вместо артикула — технический: в 1С у позиции
 * артикула нет, и он сгенерирован при загрузке. Пользователю такой не показываем.
 */
export const isMadeUpSku = (sku?: string | null): boolean =>
  !!sku && (sku.startsWith('1C-') || /^\d{8,}$/.test(sku));

/** Артикул для показа в списке: технический заменяем прочерком */
export const fmtSku = (sku?: string | null): string => (!sku || isMadeUpSku(sku) ? '—' : sku);

// ── Весовой товар ───────────────────────────────────────────────────────────
// В базе весовой товар (products.unit = 'г') хранится целыми граммами — остатки,
// резервы, движения, составы наборов, заказы поставщику. Человеку показываем
// и принимаем ввод в килограммах.

/** Товар весовой (хранится в граммах)? */
export const isWeight = (unit?: string | null): boolean => unit === 'г';

/** Подпись единицы для человека: 'г' → 'кг', иначе как есть (по умолчанию 'шт') */
export const unitLabel = (unit?: string | null): string => (unit === 'г' ? 'кг' : unit || 'шт');

/** Граммы → килограммы для показа/ввода; у штучных — как есть */
export const toDisplay = (q: number | null | undefined, unit?: string | null): number =>
  q == null ? 0 : unit === 'г' ? Number(q) / 1000 : Number(q);

/** Введённое значение (кг у весового) → целое хранимое количество (граммы) */
export const fromInput = (v: number | string | null | undefined, unit?: string | null): number => {
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(',', '.'));
  if (!Number.isFinite(n)) return 0;
  return unit === 'г' ? Math.round(n * 1000) : n;
};

/** Число количества без единицы: граммы показываются в кг (до 3 знаков) */
export const fmtQtyNum = (q: number | null | undefined, unit?: string | null): string =>
  q == null ? '—' : toDisplay(q, unit).toLocaleString('ru-RU', { maximumFractionDigits: 3 });

/**
 * Количество с единицей: 7630 г → «7,63 кг», 500 г → «0,5 кг», 3 шт → «3 шт».
 * signed — «+» у положительных; hidePcs — не печатать «шт» (как в таблицах, где у штучных
 * единица не пишется).
 */
export function fmtQty(q: number | null | undefined, unit?: string | null,
  opts: boolean | { signed?: boolean; hidePcs?: boolean } = {}): string {
  if (q == null) return '—';
  const o = typeof opts === 'boolean' ? { signed: opts } : opts;
  const n = Number(q);
  const sign = o.signed && n > 0 ? '+' : n < 0 ? '−' : '';
  const num = fmtQtyNum(Math.abs(n), unit);
  const u = unitLabel(unit);
  if (o.hidePcs && u === 'шт') return sign + num;
  return `${sign}${num} ${u}`;
}

/** Свойства NumberInput для ввода количества: у весового — кг с тремя знаками */
export const qtyInputProps = (unit?: string | null) =>
  unit === 'г'
    ? { allowDecimal: true, decimalScale: 3, decimalSeparator: ',', suffix: ' кг' }
    : { allowDecimal: false };

/** Цена весового товара хранится за грамм (1С → /1000); человеку — за килограмм */
export const priceToDisplay = (p: number | string | null | undefined, unit?: string | null): number | null =>
  p == null || p === '' ? null : unit === 'г' ? Math.round(Number(p) * 1000 * 100) / 100 : Number(p);

/** Денежное поле: копейки видны всегда; запятая и точка принимаются при вводе. */
export const priceInputProps = {
  allowDecimal: true, decimalScale: 2, fixedDecimalScale: true, decimalSeparator: ',',
};

/** Цена за кг → за грамм. Две цифры копеек за кг требуют пяти знаков за грамм. */
export const priceFromInput = (v: number | string | null | undefined, unit?: string | null, scale = 5): number | null => {
  if (v == null || v === '') return null;
  const n = Number(typeof v === 'string' ? v.replace(',', '.') : v);
  if (!Number.isFinite(n)) return null;
  const factor = 10 ** scale;
  return unit === 'г' ? Math.round((n / 1000) * factor) / factor : n;
};

/** Итог по строкам разных единиц: «12» / «12 шт + 7,63 кг» (штуки и граммы не складываем) */
export function fmtQtyTotal<T>(lines: T[], qty: (l: T) => number | null | undefined, unit: (l: T) => string | null | undefined): string {
  const by = new Map<string, number>();
  for (const l of lines) {
    const u = unit(l) || 'шт';
    by.set(u, (by.get(u) ?? 0) + Number(qty(l) ?? 0));
  }
  if (!by.size) return '0';
  if (by.size === 1 && by.has('шт')) return (by.get('шт') ?? 0).toLocaleString('ru-RU');
  return [...by].map(([u, q]) => fmtQty(q, u)).join(' + ');
}

/** Разбор строки из поля ввода количества («0,5 кг», «1 200») → хранимое значение */
export const parseQtyInput = (s: string, unit?: string | null): number =>
  fromInput(s.replace(/[^\d,.\-]/g, ''), unit);

/** Итог «всего, из них весового в граммах» → «12» / «12 шт + 7,63 кг» / «7,63 кг» */
export function pcsAndKg(total: number | null | undefined, weightG: number | null | undefined): string {
  const g = Number(weightG ?? 0);
  const pcs = Number(total ?? 0) - g;
  if (!g) return pcs.toLocaleString('ru-RU');
  return [pcs ? `${pcs.toLocaleString('ru-RU')} шт` : '', fmtQty(g, 'г')].filter(Boolean).join(' + ');
}

/** «A-01 (7630), B-02 (500)» → «A-01 (7,63), B-02 (0,5)» у весового товара */
export const fmtCells = (cells: string | null | undefined, unit?: string | null): string | null =>
  cells == null || unit !== 'г' ? cells ?? null : cells.replace(/\((-?\d+)\)/g, (_m, n) => `(${fmtQtyNum(Number(n), unit)})`);
